import { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Mail, RefreshCw } from 'lucide-react';
import { fetchQueueOverview, type QueueJobEntry, type QueueOverview } from '@/api/monitoring';
import { useErrorToast } from '@/lib/notify';

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
}

function fmtDuration(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1000) return `${(value / 1000).toFixed(2)} sn`;
  return `${value} ms`;
}

function stateTone(state: QueueJobEntry['state']): string {
  switch (state) {
    case 'completed':
      return 'border-emerald-300 text-emerald-800 bg-[linear-gradient(180deg,rgba(255,255,255,0.95)_0%,rgba(209,250,229,0.96)_42%,rgba(110,231,183,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(5,150,105,0.18),0_4px_10px_rgba(16,185,129,0.18)]';
    case 'failed':
      return 'border-rose-300 text-rose-800 bg-[linear-gradient(180deg,rgba(255,255,255,0.95)_0%,rgba(ffe4e6,0.96)_40%,rgba(251,113,133,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(190,24,93,0.18),0_4px_10px_rgba(244,63,94,0.18)]';
    case 'active':
      return 'border-blue-300 text-blue-800 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(219,234,254,0.96)_40%,rgba(96,165,250,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(30,64,175,0.18),0_4px_10px_rgba(59,130,246,0.18)]';
    case 'waiting':
      return 'border-amber-300 text-amber-800 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(254,243,199,0.96)_40%,rgba(251,191,36,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(180,83,9,0.18),0_4px_10px_rgba(245,158,11,0.18)]';
    case 'delayed':
      return 'border-violet-300 text-violet-800 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(237,233,254,0.96)_40%,rgba(167,139,250,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(109,40,217,0.18),0_4px_10px_rgba(139,92,246,0.18)]';
    default:
      return 'border-slate-300 text-slate-700 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(241,245,249,0.96)_40%,rgba(203,213,225,0.92)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(71,85,105,0.12),0_4px_10px_rgba(100,116,139,0.12)]';
  }
}

export default function QueueTab() {
  const [overview, setOverview] = useState<QueueOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  async function load() {
    try {
      setError('');
      const data = await fetchQueueOverview();
      setOverview(data);
    } catch {
      setError('Queue bilgileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const rows = overview?.jobs ?? [];
  const counts = overview?.totalCounts ?? {};
  const queueRows = overview?.queues ?? [];

  const colDefs = useMemo<ColDef<QueueJobEntry>[]>(() => [
    { headerName: 'Job', field: 'id', width: 88, cellClass: 'font-mono text-xs text-slate-700' },
    {
      headerName: 'Durum',
      field: 'state',
      width: 104,
      cellRenderer: ({ value }: ICellRendererParams<QueueJobEntry>) => (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-[3px] text-[10px] font-semibold capitalize leading-none tracking-[0.01em] ${stateTone((value as QueueJobEntry['state']) || 'unknown')}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-white/85 shadow-[0_0_0_1px_rgba(255,255,255,0.55),0_1px_2px_rgba(15,23,42,0.22)]" />
          {String(value ?? 'unknown')}
        </span>
      ),
    },
    {
      headerName: 'Konu',
      field: 'subject',
      minWidth: 220,
      flex: 1.2,
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Alıcı',
      field: 'recipients',
      minWidth: 220,
      flex: 1.2,
      valueFormatter: ({ value }) => Array.isArray(value) && value.length ? value.join(', ') : '—',
    },
    { headerName: 'Kuyruğa Giriş', field: 'queuedAt', minWidth: 165, valueFormatter: ({ value }) => fmtDate(value as string | null) },
    { headerName: 'Başlama', field: 'startedAt', minWidth: 165, valueFormatter: ({ value }) => fmtDate(value as string | null) },
    { headerName: 'Bitiş', field: 'finishedAt', minWidth: 165, valueFormatter: ({ value }) => fmtDate(value as string | null) },
    { headerName: 'Süre', field: 'durationMs', width: 90, valueFormatter: ({ value }) => fmtDuration(value as number | null) },
    { headerName: 'Deneme', field: 'attemptsMade', width: 76, cellClass: 'font-mono text-xs text-slate-700' },
    {
      headerName: 'Provider',
      field: 'provider',
      width: 88,
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Hata',
      field: 'failedReason',
      minWidth: 240,
      flex: 1,
      valueFormatter: ({ value }) => value || '—',
    },
  ], []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Queue Sayısı</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">{queueRows.length || 0}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
          <p className="text-xs text-slate-500">Waiting</p>
          <p className="mt-1 text-2xl font-semibold text-amber-700">{counts.waiting ?? 0}</p>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
          <p className="text-xs text-slate-500">Active</p>
          <p className="mt-1 text-2xl font-semibold text-blue-700">{counts.active ?? 0}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
          <p className="text-xs text-slate-500">Completed</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-700">{counts.completed ?? 0}</p>
        </div>
        <div className="rounded-xl border border-rose-100 bg-rose-50/60 p-4">
          <p className="text-xs text-slate-500">Failed</p>
          <p className="mt-1 text-2xl font-semibold text-rose-700">{counts.failed ?? 0}</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="mb-2 text-xs font-semibold text-slate-500">İzlenen Queue'lar</div>
        <div className="flex flex-wrap gap-2">
          {queueRows.map((item) => (
            <div
              key={item.queue}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700"
            >
              <span className="font-semibold">{item.queue}</span>
              <span className="text-slate-500">
                W:{item.counts.waiting ?? 0} A:{item.counts.active ?? 0} C:{item.counts.completed ?? 0} F:{item.counts.failed ?? 0}
              </span>
            </div>
          ))}
          {queueRows.length === 0 && <span className="text-xs text-slate-400">Queue bilgisi yok.</span>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm text-slate-500">
          <Mail className="h-4 w-4 text-amber-600" />
          Mail ve FTP ingest queue job'ları durum, süre ve hata bilgileriyle izlenir.
        </div>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            void load();
          }}
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Yenile
        </button>
      </div>


      <div className="ag-theme-alpine h-[560px] min-h-0 w-full rounded-xl border border-slate-200 bg-white">
        <AgGridReact<QueueJobEntry>
          modules={[AllEnterpriseModule]}
          rowData={rows}
          columnDefs={colDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          rowHeight={40}
          headerHeight={38}
          animateRows
          loading={loading}
        />
      </div>
    </div>
  );
}
